/*
 * Copyright © 2018-2025, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { useEffect, useState } from "react";
import Button from "./Button";
import { ChevronRight } from "../common/Icons/ChevronRight";
import { ChevronLeft } from "../common/Icons/ChevronLeft";

interface Props {
  currentPage?: number;
  firstPage?: number;
  lastPage?: number;
  maxVisiblePages?: number;
  selectPage(page: number): void;
  totalPages: number;
  updatePageRange?(first: number, last: number): void;
}

const Paging: React.FC<Props> = ({
  currentPage,
  firstPage: fp,
  lastPage: lp,
  maxVisiblePages = 5,
  selectPage,
  totalPages = 1,
  updatePageRange,
}: Props) => {
  const [actualPage, setActualPage] = useState<number>(1);
  const [lastPage, setLastPage] = useState<number>(1);
  const [firstPage, setFirstPage] = useState<number>(1);

  useEffect(() => {
    const page = (currentPage || 0) + 1;
    setActualPage(page);
  }, [currentPage]);

  useEffect(() => {
    if (fp && fp !== firstPage) {
      setFirstPage(fp);
    }
    if (lp && lp !== lastPage) {
      setLastPage(lp);
    }
  }, [fp, lp]);

  useEffect(() => {
    if (updatePageRange) {
      updatePageRange(firstPage, lastPage);
    }
  }, [firstPage, lastPage]);

  useEffect(() => {
    setLastPage(totalPages > maxVisiblePages ? maxVisiblePages : totalPages);
    setFirstPage(1);
  }, [totalPages, maxVisiblePages]);

  const boxes = [];

  for (let i = firstPage; i <= lastPage; i += 1) {
    boxes.push(
      <Button
        className="px-2"
        key={i}
        type="button"
        onClick={() => {
          selectPage(i - 1);
        }}
        small
        variant={actualPage === i ? "primary" : "secondary"}
      >
        {i}
      </Button>
    );
  }

  return (
    <div className="flex space-x-1">
      {firstPage > 1 ? (
        <Button
          type="button"
          onClick={() => {
            setFirstPage(firstPage - maxVisiblePages);
            const nextPageLength: number =
              totalPages - firstPage + 1 > maxVisiblePages
                ? maxVisiblePages
                : totalPages - firstPage + 1;
            setLastPage(lastPage - nextPageLength);
          }}
          variant="basic"
        >
          <ChevronLeft className="w-5 h-5 flex-shrink-0" />
        </Button>
      ) : (
        <></>
      )}
      {boxes}
      {lastPage < totalPages ? (
        <Button
          onClick={() => {
            setFirstPage(firstPage + maxVisiblePages);
            const nextPageLength: number =
              totalPages - lastPage > maxVisiblePages
                ? maxVisiblePages
                : totalPages - lastPage;
            setLastPage(lastPage + nextPageLength);
          }}
          variant="basic"
        >
          <ChevronRight className="w-5 h-5 flex-shrink-0" />
        </Button>
      ) : (
        <></>
      )}
    </div>
  );
};

Paging.defaultProps = {
  maxVisiblePages: 5,
  currentPage: 0,
};

export default Paging;
